import {Component, ViewEncapsulation} from '@angular/core';
import {JSONSchema7} from 'json-schema';
import {TreeNode} from 'primeng/api';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {
    ECpmCReferenceExpressionSourceTypeApiEnum,
    RECpmCReferenceExpressionSourceTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-global/cpmc-reference-expression.api-enums';
import {IBaseModalComponent} from '../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    ConfirmationModalComponent,
    ConfirmationModalConfig
} from '../../../../sdk/src/modals/confirm-modal/confirm-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {JsonSchemaUtils} from '../../../../sdk/src/utils/json-schema.utils';
import {StringUtils} from '../../../../sdk/src/utils/string.util';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {ReferenceExpressionUtils} from '../../utils/reference-expression.utils';
import {
    NewEditObjectStoreEntryModalComponent,
    NewEditObjectStoreEntryModalConfig
} from '../new-edit-object-store-entry-modal/new-edit-object-store-entry-modal.component';

export class ReferenceExpressionModalConfig {
    constructor(
        public title: string,
        public schema: JSONSchema7,
        public rootSchema: JSONSchema7,
        public readOnly: boolean,
        public allowedSourceTypes: ECpmCReferenceExpressionSourceTypeApiEnum[],
        public sourceIdentifiersGenerator?: (sourceType: ECpmCReferenceExpressionSourceTypeApiEnum) => Promise<
            {
                label: string;
                value: string;
            }[]
        >,
        public attributeNamesGenerator?: (
            sourceType: ECpmCReferenceExpressionSourceTypeApiEnum
        ) => Promise<TreeNode<string>[]>,
        public objectStoreUUIDGenerator?: (linkObjectType: string) => string | undefined,
        public expression?: string | null,
        public defaultSourceType?: ECpmCReferenceExpressionSourceTypeApiEnum,
        public defaultSourceIdentifier?: string,
        public defaultKeyLabel?: string,
        public defaultKeyCreator?: (key: string) => Promise<void>,
        public continueConfirmation?: ConfirmationModalConfig,
        public continueAsync?: (expression: string) => Promise<boolean | void>
    ) {}
}
export class ReferenceExpressionModalResult {
    constructor(public expression?: string | null) {}
}

@Component({
    selector: 'app-reference-expression-modal',
    templateUrl: './reference-expression-modal.component.html',
    styleUrls: ['./reference-expression-modal.component.scss'],
    imports: [PrimeNgImportsModule],
    encapsulation: ViewEncapsulation.None
})
export class ReferenceExpressionModalComponent extends IBaseModalComponent<
    ReferenceExpressionModalComponent,
    ReferenceExpressionModalConfig
> {
    protected contentMinHeight = '20rem';

    protected sourceTypeOptions: {label: string; value: ECpmCReferenceExpressionSourceTypeApiEnum}[] = [];
    protected sourceType?: ECpmCReferenceExpressionSourceTypeApiEnum;
    protected lastSourceType?: ECpmCReferenceExpressionSourceTypeApiEnum;

    protected sourceIdentifierOptions: {label: string; value: string}[] = [];
    protected sourceIdentifier?: string;

    protected attributeNameOptions: TreeNode<string>[] = [];
    protected attributeName?: TreeNode<string>;

    protected get expectedTypeLabel(): string {
        const schema = this.getExpectedSchema();
        if (!schema) return 'Unknown';

        const type = Array.isArray(schema.type) ? schema.type.join(' | ') : schema.type;

        return type ?? 'Unknown';
    }

    protected get expectedObjectSchemaKey(): string | undefined {
        const schema = this.getExpectedSchema();
        return schema ? this.getExpectedObjectSchemaKey(schema) : undefined;
    }

    protected get expectedSchemaDescription(): string | undefined {
        return this.getExpectedSchema()?.description;
    }

    protected get canCreateDefaultKey(): boolean {
        return !!(
            !this.attributeName &&
            !this.config.data?.readOnly &&
            this.config.data?.defaultKeyLabel &&
            this.config.data?.defaultKeyCreator &&
            (this.sourceType ?? this.config.data?.defaultSourceType) &&
            (this.sourceIdentifier ?? this.config.data?.defaultSourceIdentifier)
        );
    }

    protected updateAttributeOptionTitle(element: HTMLElement, label?: string): void {
        if (label && element.scrollWidth > element.clientWidth) element.title = label;
        else element.removeAttribute('title');
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get dialog service parameters.
     * @param config modal configuration
     */
    public static getDialogServiceParameters(
        config: ReferenceExpressionModalConfig
    ): DynamicDialogConfig<ReferenceExpressionModalConfig> {
        return {
            modal: true,
            header: `Bind to Reference: ${config.title}`,
            data: config,
            width: '40rem',
            draggable: true
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.config.data?.allowedSourceTypes.forEach((x) => {
            this.sourceTypeOptions.push({
                label: `${x} (${StringUtils.toHumanFormatedStr(RECpmCReferenceExpressionSourceTypeApiEnum[x].toString())})`,
                value: x
            });
        });

        if (!this.sourceType && this.config.data?.defaultSourceType) {
            if (this.sourceTypeOptions.find((x) => x.value == this.config.data!.defaultSourceType))
                this.sourceType = this.config.data.defaultSourceType;
        }

        await this.onSourceTypeChanged();

        if (this.sourceType && !this.sourceIdentifier && this.config.data?.defaultSourceIdentifier) {
            if (this.sourceIdentifierOptions.find((x) => x.value == this.config.data!.defaultSourceIdentifier))
                this.sourceIdentifier = this.config.data.defaultSourceIdentifier;
        }

        if (this.config.data?.expression) {
            try {
                const parsedExpressions = ReferenceExpressionUtils.parseExpressions(this.config.data.expression);

                /// Only take the first expression from start to finish
                if (
                    parsedExpressions.length == 1 &&
                    parsedExpressions[0].start == 0 &&
                    parsedExpressions[0].end == this.config.data.expression.length
                ) {
                    this.sourceType = parsedExpressions[0].sourceType;
                    this.sourceIdentifier = parsedExpressions[0].sourceIdentifier;

                    await this.onSourceTypeChanged();

                    const foundAttributeNode = TreeUtils.findTreeNodeByKey(
                        this.attributeNameOptions,
                        parsedExpressions[0].attributeName
                    );

                    this.attributeName = foundAttributeNode;
                }
            } catch (error) {
                console.log(error);
                // do nothing
            }
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On cancel
     */
    protected onCancel() {
        this.ref.close({expression: undefined} as ReferenceExpressionModalResult);
    }
    /**
     * On continue
     */
    protected onContinue() {
        const attributeName = this.attributeName?.data ?? this.attributeName?.key;
        if (!attributeName || !this.sourceType || !this.sourceIdentifier) return;

        this.ref.close({
            expression: ReferenceExpressionUtils.buildExpression(attributeName, this.sourceType, this.sourceIdentifier)
        } as ReferenceExpressionModalResult);
    }

    /**
     *  Handles the primary action, which may involve continuing with the current attribute or creating a default key.
     *
     * @returns A promise that resolves when the primary action has been handled.
     */
    protected async onPrimaryAction() {
        if (!this.attributeName) {
            const sourceType = this.sourceType ?? this.config.data?.defaultSourceType;
            const sourceIdentifier = this.sourceIdentifier ?? this.config.data?.defaultSourceIdentifier;

            if (!this.canCreateDefaultKey || !sourceType || !sourceIdentifier) {
                return;
            }

            const normalizedKey = StringUtils.toSnakeCase(this.config.data?.defaultKeyLabel ?? '');
            let start = 0;
            let end = normalizedKey.length;

            while (start < end && normalizedKey[start] === '_') {
                start++;
            }
            while (end > start && normalizedKey[end - 1] === '_') {
                end--;
            }

            const trimmedNormalizedKey = normalizedKey.slice(start, end);
            if (!trimmedNormalizedKey) {
                return;
            }

            this.showLoading = true;
            this.loadingStr = 'Creating default key...';

            try {
                await this.config.data!.defaultKeyCreator!(trimmedNormalizedKey);
                this.ref.close({
                    expression: ReferenceExpressionUtils.buildExpression(
                        trimmedNormalizedKey,
                        sourceType,
                        sourceIdentifier
                    )
                } as ReferenceExpressionModalResult);
            } catch (err: unknown) {
                this.globalsService.messageService.add({
                    key: this.overrideMessagesKey,
                    severity: 'error',
                    summary: 'Unable to create default key',
                    detail: this.getErrorDetail(err)
                });
            } finally {
                this.showLoading = false;
            }

            return;
        }

        const attributeName = this.attributeName.data ?? this.attributeName.key;
        if (!attributeName || !this.sourceType || !this.sourceIdentifier) {
            return;
        }
        const expression = ReferenceExpressionUtils.buildExpression(
            attributeName,
            this.sourceType,
            this.sourceIdentifier
        );

        const continueWithExpression = async () => {
            if (this.config.data?.continueAsync) {
                this.showLoading = true;
                this.loadingStr = 'Updating task...';

                try {
                    const canClose = await this.config.data.continueAsync(expression);
                    if (canClose === false) {
                        return;
                    }
                } catch (err: unknown) {
                    this.globalsService.messageService.add({
                        key: this.overrideMessagesKey,
                        severity: 'error',
                        summary: 'Unable to update task',
                        detail: this.getErrorDetail(err)
                    });
                    return;
                } finally {
                    this.showLoading = false;
                }
            }

            this.ref.close({expression} as ReferenceExpressionModalResult);
        };

        const confirmation = this.config.data?.continueConfirmation;

        if (!confirmation) {
            await continueWithExpression();
            return;
        }

        this.globalsService.dialogService
            .open(ConfirmationModalComponent, ConfirmationModalComponent.getDialogServiceParameters(confirmation))
            .onClose.subscribe((confirmed: boolean) => {
                if (confirmed) {
                    void continueWithExpression();
                }
            });
    }

    private readonly getErrorDetail = (err: unknown): string => {
        if (err && typeof err === 'object') {
            const maybeError = err as {error?: {message?: string}; message?: string};
            return maybeError.error?.message ?? maybeError.message ?? 'Unknown';
        }
        return 'Unknown';
    };

    /**
     * Display the current value in a read-only modal if it exists and is valid.
     */
    protected onDisplayCurrentValue() {
        const entryKey = this.attributeName?.data ?? this.attributeName?.key;
        const resolvedSourceIdentifier =
            (this.attributeName as (TreeNode<string> & {sourceIdentifier?: string}) | undefined)?.sourceIdentifier ??
            this.sourceIdentifier;
        const linkObjectUUID = this.config.data?.objectStoreUUIDGenerator?.(resolvedSourceIdentifier!);

        if (
            this.sourceType !== ECpmCReferenceExpressionSourceTypeApiEnum.OBJECT_STORE ||
            !resolvedSourceIdentifier ||
            !entryKey ||
            !linkObjectUUID
        )
            return;

        this.globalsService.dialogService.open(
            NewEditObjectStoreEntryModalComponent,
            NewEditObjectStoreEntryModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    linkObjectType: resolvedSourceIdentifier,
                    linkObjectUUID,
                    forceExistingRefresh: true,
                    entryKey
                } as NewEditObjectStoreEntryModalConfig
            })
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * On source type changed
     */
    protected async onSourceTypeChanged() {
        this.showLoading = true;

        if (this.sourceType != this.lastSourceType) {
            await this.asyncCritical(async () => {
                this.sourceIdentifierOptions =
                    this.sourceType && this.config.data?.sourceIdentifiersGenerator
                        ? await this.config.data.sourceIdentifiersGenerator(this.sourceType)
                        : [];

                this.attributeNameOptions =
                    this.sourceType && this.config.data?.attributeNamesGenerator
                        ? await this.config.data.attributeNamesGenerator(this.sourceType)
                        : [];

                this.lastSourceType = this.sourceType;

                if (!this.attributeNameOptions.some((x) => x.key == this.attributeName?.key))
                    this.attributeName = undefined;
            });
        }

        this.showLoading = false;
    }

    /**
     * Get the expected JSON schema for the current reference expression.
     */
    private getExpectedSchema(): JSONSchema7 | undefined {
        let schema = this.resolveSchemaRef(this.config.data?.schema);
        if (!schema) return undefined;

        schema = this.resolveNullableAnyOfSchema(schema);
        if (!schema) return undefined;

        return this.normalizeNullableSchemaType(schema);
    }

    /**
     * Resolve a JSON schema reference ($ref) if present.
     * @param schema The JSON schema that may contain a $ref to resolve.
     * @returns The resolved JSON schema, or undefined if it cannot be resolved.
     */
    private resolveSchemaRef(schema?: JSONSchema7): JSONSchema7 | undefined {
        // The modal receives property schemas that can be only references to definitions in the root schema.
        if (!schema?.$ref || !this.config.data?.rootSchema) return schema;

        return JsonSchemaUtils.resolveRef(schema.$ref, this.config.data.rootSchema);
    }

    /**
     * Resolve a JSON schema that uses the nullable anyOf pattern, if applicable.
     * @param schema The JSON schema that may use the nullable anyOf pattern.
     * @returns The resolved non-null JSON schema, or undefined if it cannot be resolved.
     */
    private resolveNullableAnyOfSchema(schema: JSONSchema7): JSONSchema7 | undefined {
        if (
            schema?.anyOf?.length === 2 &&
            schema.anyOf.some((candidate) => (candidate as JSONSchema7).type === 'null')
        ) {
            // Nullable values can be represented as anyOf: [{...real schema...}, {type: 'null'}].
            const nonNullSchema = schema.anyOf.find((candidate) => (candidate as JSONSchema7).type !== 'null');
            if (!nonNullSchema || typeof nonNullSchema === 'boolean') return undefined;

            return this.resolveSchemaRef(nonNullSchema);
        }

        return schema;
    }

    /**
     * Normalize a JSON schema that uses the nullable type pattern, if applicable.
     * @param schema The JSON schema that may use the nullable type pattern.
     * @returns The normalized JSON schema with the nullable type removed.
     */
    private normalizeNullableSchemaType(schema: JSONSchema7): JSONSchema7 {
        // Nullable values can also be represented as type: ['string', 'null']; only display the usable type.
        if (!Array.isArray(schema.type)) return schema;

        const nonNullTypes = schema.type.filter((candidate) => candidate !== 'null');
        if (nonNullTypes.length === schema.type.length) return schema;
        if (nonNullTypes.length === 0) {
            const normalizedSchema = {...schema};
            delete normalizedSchema.type;
            return normalizedSchema;
        }

        return {
            ...schema,
            type: nonNullTypes.length === 1 ? nonNullTypes[0] : nonNullTypes
        };
    }

    /**
     * Get the expected object schema key from a JSON schema, considering nullable and array types.
     * @param schema The JSON schema that may represent an object or an array of objects.
     * @returns The expected object schema key, or undefined if it cannot be determined.
     */
    private getExpectedObjectSchemaKey(schema: JSONSchema7): string | undefined {
        const type = Array.isArray(schema.type) ? schema.type.find((candidate) => candidate !== 'null') : schema.type;
        let objectSchema: JSONSchema7 = schema;
        if (type === 'array') {
            if (!schema.items || schema.items === true || Array.isArray(schema.items)) {
                return undefined;
            }
            objectSchema = schema.items;
        }

        const rootSchema = this.config.data?.rootSchema;
        if (objectSchema.$ref && rootSchema) {
            const resolvedSchema = JsonSchemaUtils.resolveRef(objectSchema.$ref, rootSchema);
            if (!resolvedSchema) return undefined;

            objectSchema = resolvedSchema;
        }

        return objectSchema?.$id?.replace('.schema.json', '');
    }
}
